import type {TabProps} from 'shells/control-bar/page-control-bar';
import type {Color as BodyColor} from 'shells/typography/body';

import type {
    PeepRelatedLeadFragment as Lead,
    PeepRelatedLeadStatsFragment,
    PeepRelatedLeadStatFragment_value_formattedValue as FormattedValue,
} from 'nutshell-graphql-types';

import type {WidgetLeadStatus} from './leads-widget';

type StatusCounts = {
    openCount: number;
    wonCount: number;
    lostCount: number;
    cancelledCount: number;
};

export function getTabOptions(
    selectedTab: WidgetLeadStatus,
    handleTabChange: (tab: WidgetLeadStatus) => void,
    statusCounts: StatusCounts,
    hideCount: boolean
): TabProps {
    // These ids match the LeadTab type in leads-widget.jsx
    const tabs = [];
    if (statusCounts.openCount > 0) {
        tabs.push({
            id: 'Open',
            label: 'Open',
            count: hideCount ? undefined : String(statusCounts.openCount),
        });
    }

    if (statusCounts.wonCount > 0) {
        tabs.push({
            id: 'Won',
            label: 'Won',
            count: hideCount ? undefined : String(statusCounts.wonCount),
        });
    }

    if (statusCounts.lostCount > 0) {
        tabs.push({
            id: 'Lost',
            label: 'Lost',
            count: hideCount ? undefined : String(statusCounts.lostCount),
        });
    }

    if (statusCounts.cancelledCount > 0) {
        tabs.push({
            id: 'Cancelled',
            label: 'Cancelled',
            count: hideCount ? undefined : String(statusCounts.cancelledCount),
        });
    }

    return {
        options: tabs,
        selectedTab,
        onChangeTab: (tab: string) => {
            // @ts-expect-error
            handleTabChange(tab);
        },
    };
}

export function getLeadsList(
    leads: Lead[] | null | undefined,
    selectedTab: WidgetLeadStatus
): Lead[] {
    if (!leads) {
        return [];
    }

    switch (selectedTab) {
        case 'Open':
            return leads.filter((lead) => lead.status === 'OPEN' || lead.status === 'PENDING');
        case 'Won':
            return leads.filter((lead) => lead.status === 'WON');
        case 'Lost':
            return leads.filter((lead) => lead.status === 'LOST');
        case 'Cancelled':
            return leads.filter((lead) => lead.status === 'CANCELLED');
    }
}

export function getStatusCounts(
    stats: PeepRelatedLeadStatsFragment | null | undefined
): StatusCounts {
    return {
        openCount: stats?.openAndPending?.count ?? 0,
        wonCount: stats?.all?.find((stat) => stat.status === 'WON')?.count ?? 0,
        lostCount: stats?.all?.find((stat) => stat.status === 'LOST')?.count ?? 0,
        cancelledCount: stats?.all?.find((stat) => stat.status === 'CANCELLED')?.count ?? 0,
    };
}

export function getFirstTabWithCount(statusCounts: StatusCounts): WidgetLeadStatus {
    if (statusCounts.openCount > 0) return 'Open';
    if (statusCounts.wonCount > 0) return 'Won';
    if (statusCounts.lostCount > 0) return 'Lost';
    if (statusCounts.cancelledCount > 0) return 'Cancelled';
    // Default to open if no tabs have counts
    return 'Open';
}

export function getCountForSelectedTab(statusCounts: StatusCounts, tab: WidgetLeadStatus): number {
    switch (tab) {
        case 'Open':
            return statusCounts.openCount;
        case 'Won':
            return statusCounts.wonCount;
        case 'Lost':
            return statusCounts.lostCount;
        case 'Cancelled':
            return statusCounts.cancelledCount;
    }
}

export function getStatsValues(stats: PeepRelatedLeadStatsFragment | null | undefined) {
    return [
        {
            status: 'Open',
            stat: stats?.openAndPending?.value?.formattedValue,
        },
        {
            status: 'Won',
            stat: stats?.all?.find((stat) => stat.status === 'WON')?.value?.formattedValue,
        },
        {
            status: 'Lost',
            stat: stats?.all?.find((stat) => stat.status === 'LOST')?.value?.formattedValue,
        },
        {
            status: 'Cancelled',
            stat: stats?.all?.find((stat) => stat.status === 'CANCELLED')?.value?.formattedValue,
        },
        // Only return stats that have a value
    ].filter((item): item is {status: WidgetLeadStatus; stat: FormattedValue} =>
        Boolean(item.stat)
    );
}

export function getStatColor(status: WidgetLeadStatus): BodyColor {
    switch (status) {
        case 'Won':
            return 'lead';
        default:
            return 'dark';
    }
}

export function getLeadsListLink(
    peepId: string,
    entityType?: string,
    selectedTab?: WidgetLeadStatus
): string {
    const entityFilter = entityType === 'Contact' ? 'contacts' : 'accounts';
    const pendingFilter = selectedTab === 'Open' ? '&filter[status][data][1][data]=Pending' : '';

    return `/leads?filter[${entityFilter}][data][0][data]=${peepId}&filter[status][data][0][data]=${selectedTab}${pendingFilter}&view=list`;
}

export function getChildLeadsListLink(
    childAccountIds: string[] | undefined,
    selectedTab: WidgetLeadStatus
): string {
    if (!childAccountIds) {
        return '';
    }

    const pendingFilter = selectedTab === 'Open' ? '&filter[status][data][1][data]=Pending' : '';
    const childAccountFilter = childAccountIds
        .map((id, index) => `&filter[accounts][data][${index}][data]=${id}`)
        .join('');

    return `/leads?filter[accounts][anyAll]=any&filter[accounts][data][0][isNot]=false${childAccountFilter}&filter[status][data][0][data]=${selectedTab}${pendingFilter}&view=list`;
}
